import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Handle, Position } from "@xyflow/react";
import { ChevronDown, ChevronUp, MoreHorizontal } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import PlatformBadge from "../common/platform-badge";
import ServiceTypePill from "../common/service-type-pill";
import TroubleshootingInfo from "../common/troubleshooting-info";
import CollapsibleTagList from "./collapsible-tag-list";

const CustomCardNode = ({ data, setNodeDimensions = () => {} }) => {
  const cardRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 150, height: 50 });

  useEffect(() => {
    if (cardRef.current) {
      const { width, height } = cardRef.current.getBoundingClientRect();
      setDimensions({ width, height });
      setNodeDimensions(data.title, { width, height }); // Update node dimensions in the layout calculation
    }
  }, [cardRef, data.id, setNodeDimensions]);

  return (
    <>
      <Card ref={cardRef} className="border rounded-t-lg rounded-b-none">
        <CardHeader className="border-0">
          <ServiceTypePill type={data.type} />

          <CardTitle>{data.title || "Unknown Service"}</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-lg bg-transparent ml-auto"
          >
            <MoreHorizontal className="size-5" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 relative">
            <div className="flex flex-col justify-start mr-10">
              <span className="text-xs text-gray-400">Platform</span>
              <PlatformBadge platform={data.platform} />
            </div>

            <div className="flex flex-col">
              <span className="text-xs text-gray-400">Connections</span>
              <div className="flex items-center">
                <ChevronUp className="text-green-500 w-4 h-4" />
                <span className="text-sm ml-1">{data.inbound}</span>
                <ChevronDown className="text-yellow-500 w-4 h-4 ml-4" />
                <span className="text-sm ml-1">{data.outbound}</span>
              </div>
            </div>

            <div className="flex flex-col justify-start mr-10">
              <span className="text-xs text-gray-400">Version</span>
              <span className="block text-sm">{data.version || "0.2.1"}</span>
            </div>

            <div className="flex flex-col">
              <span className="text-xs text-gray-400">Troubleshooting</span>
              <TroubleshootingInfo {...data} />
            </div>
            <div className="absolute w-full h-full text-center flex items-center justify-center bg-navbarBackground/80 z-[999] ">
              <p>Unknown Service</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="border bg-[#15171C] rounded-b-sm">
        <div className="flex items-center h-full p-2">
          {data?.tags?.length > 0 ? (
            <CollapsibleTagList
              tags={data?.tags?.sort((a, b) => a.length - b.length) || []}
              availableWidth={dimensions.width - 32}
            />
          ) : (
            <span className="text-xs text-gray-400">No tags</span>
          )}
        </div>
      </div>

      {/* React Flow Handles for connection */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-2 h-2 bg-gray-400"
      />
      <Handle
        type="target"
        position={Position.Left}
        className="w-2 h-2 bg-gray-400"
      />
    </>
  );
};

export default CustomCardNode;
